import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { connect, useSelector } from 'react-redux';
import queryString, { stringify } from 'query-string';

import { cn } from 'helpers/classnames';
import { evReturnInitiateClick } from 'events/returns';
import useMartyContext from 'hooks/useMartyContext';
import OrderHistoryTableExpandedItemRow from 'components/account/OrderHistoryOverview/OrderHistoryTableExpandedItemRow.jsx';
import { Loader } from 'components/Loader';
import { track } from 'apis/amethyst';
import { evOrderHistoryActionClick, evOrderHistoryToggleShowRow, evOrderNumberClick } from 'events/account';
import { getAmethystPageType } from 'helpers/analytics';
import { getAllReturnableItemUniqueIds, getPaginationInformation, isOrderReturnable } from 'helpers/MyAccountUtils';
import { ORDER_HISTORY_ACTION_TYPE, ORDER_LINK_TYPE, RETURN_INITIATE_SOURCE } from 'constants/amethystEnums';
import { toggleOrderCollapse } from 'actions/account/orders';

const { UNKNOWN_ORDER_LINK, ORDER_TEXT_LINK, ORDER_IMAGE_LINK } = ORDER_LINK_TYPE;

import css from 'styles/components/account/orderHistory/orderHistoryTableExpanded.scss';

export const OrderHistoryTableExpanded = ({
  orders,
  isLoading,
  onClickOrderLink,
  toggleOrderCollapse,
  uncollapsedOrderHistoryRowMap = {},
  keepExchangeOrdersFromReturnCancel,
  account
}) => {
  const {
    testId,
    amethystTrack,
    marketplace: { dataMaskPiiElements }
  } = useMartyContext();
  const pageView = useSelector(state => state.pageView);
  const { pageType, location: { search, query = {} } = {} } = pageView;
  const locationParams = useLocation();

  const handleOrderNumberClick = e => {
    const { orderId, orderItemCount, orderIndex, itemIndex, linkType = UNKNOWN_ORDER_LINK } = e.currentTarget.dataset;
    onClickOrderLink(); // keeps history
    track(() => [
      evOrderNumberClick,
      {
        orderId,
        linkType,
        orderIndex,
        itemIndex,
        totalItems: orderItemCount,
        pageNumber: query.page,
        sourcePage: getAmethystPageType(pageType)
      }
    ]);
  };

  const handleShowHideButtonClick = e => {
    const { orderId, expanded } = e.currentTarget.dataset;
    const isExpanded = expanded === 'true';
    amethystTrack(() => [
      evOrderHistoryToggleShowRow,
      {
        wasHidden: !isExpanded,
        sourcePage: getAmethystPageType(pageType)
      }
    ]);
    toggleOrderCollapse(orderId);
  };

  const onActionClick = e => {
    const { orderHistoryActionType } = e.currentTarget.dataset;
    amethystTrack(() => [
      evReturnInitiateClick,
      {
        returnInitiateSource: RETURN_INITIATE_SOURCE.ACCOUNT_PAGE_RETURN_ITEMS_BUTTON
      }
    ]);
    amethystTrack(() => [
      evOrderHistoryActionClick,
      {
        orderHistoryActionType,
        product: {},
        sourcePage: getAmethystPageType(pageType)
      }
    ]);
  };
  const email = account?.customerInfo?.email || '';
  const queryParams = queryString.parse(locationParams.search);

  if (isLoading) {
    return <Loader />;
  } else if (!orders || !orders.length) {
    if (queryParams?.search) {
      return (
        <div className={css.orderHistoryNoOrderSearchContainer}>
          <p className={css.noOrderForAccount}>
            Hmmm, we couldn't find "{queryParams.search}" in account {email || ''}
          </p>
          <div>
            <p className={css.orderHistoryNoOrderSearchTextList}>Try the following:</p>
            <ul className={css.orderHistoryNoOrderListWrapper}>
              <li className={css.orderHistoryNoOrderList}>Check that your entered keyword(s) is correct</li>
              <li className={css.orderHistoryNoOrderList}>Verify that you're logged into the correct account</li>
              <li className={css.orderHistoryNoOrderList}>
                Search with keywords like...
                <ul className={css.orderHistoryNoOrderListWrapper}>
                  <li className={css.orderHistoryNoOrderList}>Brand</li>
                  <li className={css.orderHistoryNoOrderList}>Style name</li>
                  <li className={css.orderHistoryNoOrderList}>Color</li>
                  <li className={css.orderHistoryNoOrderList}>Product type (e.g. shirt, sneakers, pants)</li>
                </ul>
              </li>
            </ul>
          </div>
        </div>
      );
    } else {
      return <p className={css.emptyOrderHistory}>Your order history is empty.</p>;
    }
  } else {
    return orders.map(({ orderId, orderDate, lineItems, pricing: { totalCharged } = {} }, orderIndex) => {
      const { currentPage, pageSize } = getPaginationInformation(search);
      const orderLink = `/orders/${orderId}?${queryString.stringify({
        ohPage: currentPage,
        ohPageSize: pageSize
      })}`;
      const listOfReturnableItemUniqueIds = getAllReturnableItemUniqueIds(lineItems);
      const returnQueryString = stringify({
        // only preselect items if the order has one returnable item, otherwise make the customer choose what to return
        preselectedItems: listOfReturnableItemUniqueIds.length > 1 ? undefined : listOfReturnableItemUniqueIds,
        returnMethod: 'return'
      });
      const returnLink = `${orderLink}&${returnQueryString}`;
      const isExpandedInOrderHistory = uncollapsedOrderHistoryRowMap[orderId];
      const containsItemsToReturn = isOrderReturnable(lineItems);

      return (
        <article className={css.orderWrapper} data-test-id={testId('orderHistoryEntry')} key={orderId}>
          <section className={css.orderInformationContainer}>
            <dl className={css.orderInformation}>
              <div className={cn(css.orderDateColumn, 'zappos:w-[16em]')}>
                <dt className="zappos:body-m">Order Date</dt>
                <dd className="zappos:body-l" data-cs-mask={dataMaskPiiElements} data-test-id={testId('orderDate')}>
                  {orderDate}
                </dd>
              </div>
              <div className={css.orderNumberColumn}>
                <dt className="zappos:body-m">Order Number</dt>
                <dd className="zappos:body-l" data-cs-mask={dataMaskPiiElements}>
                  <Link
                    className="zappos:body-l"
                    to={returnLink || orderLink}
                    data-test-id={testId('orderLink')}
                    data-order-id={orderId}
                    data-order-item-count={lineItems.length}
                    data-order-index={orderIndex}
                    data-link-type={ORDER_TEXT_LINK}
                    onClick={handleOrderNumberClick}
                  >
                    #{orderId}
                  </Link>
                </dd>
              </div>
              <div className={css.totalChargedColumn}>
                <dt className="zappos:body-m">Total</dt>
                <dd className="zappos:body-l" data-cs-mask={dataMaskPiiElements} data-test-id={testId('totalCharged')}>
                  {totalCharged}
                </dd>
              </div>
            </dl>
            <div className={css.showHideButtonWrapper}>
              <button
                onClick={handleShowHideButtonClick}
                data-order-id={orderId}
                data-expanded={isExpandedInOrderHistory}
                className={cn(css.expanded, 'zappos:body-l-bold')}
                aria-expanded={isExpandedInOrderHistory}
                aria-label="Toggle full order history view"
                type="button"
                data-test-id={testId('toggleOrderView')}
              ></button>
            </div>
          </section>
          <section
            className={cn(css.productContainer, {
              [css.collapsed]: !isExpandedInOrderHistory
            })}
          >
            {!isExpandedInOrderHistory ? (
              <div className={css.imageContainer} data-cs-mask={dataMaskPiiElements}>
                {lineItems.map((item, itemIndex) => {
                  const { product } = item;
                  const { retinaImageUrl, name } = product;
                  return (
                    <Link
                      key={`${item.orderId}-${itemIndex}`}
                      aria-hidden={true}
                      tabIndex={-1}
                      to={orderLink}
                      className={css.mProduct}
                      onClick={handleOrderNumberClick}
                      data-link-type={ORDER_IMAGE_LINK}
                      data-order-id={orderId}
                      data-test-id={testId('productImageLink')}
                    >
                      <div className={css.image}>
                        <img src={retinaImageUrl} alt={name} />
                      </div>
                    </Link>
                  );
                })}
              </div>
            ) : (
              lineItems.map((item, itemIndex) => (
                <OrderHistoryTableExpandedItemRow
                  useNewCancellableFlag={!!keepExchangeOrdersFromReturnCancel}
                  key={`${item.orderId}-${itemIndex}`}
                  orderLink={orderLink}
                  handleOrderNumberClick={handleOrderNumberClick}
                  orderItemCount={lineItems.length}
                  item={item}
                  orderId={orderId}
                  index={itemIndex}
                  orderIndex={orderIndex}
                  pageType={pageType}
                />
              ))
            )}
            {!isExpandedInOrderHistory && (
              <div className={css.actionsColumn}>
                {containsItemsToReturn && (
                  <Link
                    className="zappos:body-m-medium"
                    data-order-history-action-type={ORDER_HISTORY_ACTION_TYPE.ORDER_HISTORY_RETURN_ALL}
                    onClick={onActionClick}
                    to={`${orderLink}&${returnQueryString}`}
                  >
                    Return Item(s)
                  </Link>
                )}
              </div>
            )}
          </section>
        </article>
      );
    });
  }
};

const mapDispatchToProps = {
  toggleOrderCollapse
};

const mapStateToProps = state => ({
  account: state.account
});

export default connect(mapStateToProps, mapDispatchToProps)(OrderHistoryTableExpanded);
